import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { useEffect, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

/**
 * Inspector reinstate confirmation dialog (AUT-125). Mirrors
 * `DeactivateInspectorDialog` shape minus the active-orders warning —
 * reinstating restores order-taking ability, no active-orders concern.
 * Green confirm (success colour) to signal the constructive direction.
 */
const MAX_REASON = 500;

const schema = z.object({
  reason: z
    .string()
    .trim()
    .min(1, 'inspectors.reactivateDialog.reasonRequired')
    .max(MAX_REASON, 'inspectors.reactivateDialog.reasonMax'),
});

type ReactivateForm = z.infer<typeof schema>;

export interface ReactivateInspectorDialogProps {
  open: boolean;
  loading?: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

export const ReactivateInspectorDialog = ({
  open,
  loading = false,
  onConfirm,
  onCancel,
}: ReactivateInspectorDialogProps): ReactElement => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<ReactivateForm>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { reason: '' },
  });

  useEffect(() => {
    if (open) reset({ reason: '' });
  }, [open, reset]);

  const submit = ({ reason }: ReactivateForm): void => {
    if (loading) return;
    onConfirm(reason.trim());
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{t('inspectors.reactivateDialog.title')}</DialogTitle>
      <form onSubmit={handleSubmit(submit)}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <DialogContentText>{t('inspectors.reactivateDialog.description')}</DialogContentText>
          <TextField
            {...register('reason')}
            fullWidth
            multiline
            minRows={2}
            size="small"
            disabled={loading}
            label={t('inspectors.reactivateDialog.reasonLabel')}
            error={Boolean(errors.reason)}
            helperText={errors.reason ? t(errors.reason.message ?? '') : ' '}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="success"
            disabled={loading || !isValid}
            loading={loading}
          >
            {t('inspectors.reactivateDialog.confirm')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
